import { z } from 'zod';

/**
 * SKU FMV forecast DTOs (plans/sku-fmv-forecast.md, sku-fmv-forecast-plan.md
 * Task 12).
 *
 * Backs:
 *   GET /api/skus/:id/fmv-forecast   -> skuFmvForecastResponseSchema
 *   GET /api/fmv-forecast/runs       -> fmvForecastRunListResponseSchema
 *
 * Ops-only surface: db/schema/792_sku_fmv_forecast.sql grants SELECT on
 * sku_fmv_forecast_run/sku_fmv_forecast_point only to ops_readonly and
 * ops_admin, so access control is enforced at the Postgres role level, not
 * duplicated here.
 */

export const fmvForecastConfidenceGradeSchema = z.enum(['A', 'B', 'C', 'D']);
export type FmvForecastConfidenceGrade = z.infer<typeof fmvForecastConfidenceGradeSchema>;

export const fmvForecastRunStatusSchema = z.enum(['running', 'complete', 'failed']);
export type FmvForecastRunStatus = z.infer<typeof fmvForecastRunStatusSchema>;

/**
 * SIL-1094: what triggered this run. 'live' is the weekly scheduled run --
 * the only kind that feeds the per-SKU forecast panel and the client-safe
 * forecast view (db/schema/897_sku_fmv_forecast_run_kind.sql). 'backfill'
 * is a point-in-time {"cmd":"backfill","as_of":...} invocation. 'candidate'
 * is reserved for Phase 3 tuning runs (not yet written by any caller).
 */
export const fmvForecastRunKindSchema = z.enum(['live', 'backfill', 'candidate']);
export type FmvForecastRunKind = z.infer<typeof fmvForecastRunKindSchema>;

export const fmvForecastRunSchema = z.object({
  id: z.string().uuid(),
  modelVersion: z.string(),
  gitSha: z.string().nullable(),
  /**
   * Calendar day (UTC) this run modeled, 'YYYY-MM-DD' - distinct from
   * startedAt/completedAt, which are wall-clock pipeline execution time.
   * Always "today" for a live run; an explicit past date for a backfill
   * run (db/schema/824_sku_fmv_forecast_run_as_of.sql).
   */
  asOf: z.string(),
  runKind: fmvForecastRunKindSchema,
  startedAt: z.string(),
  completedAt: z.string().nullable(),
  status: fmvForecastRunStatusSchema,
  /**
   * Rolling-origin backtest accuracy, per horizon bucket per asset class,
   * e.g. {"gpu":{"h3":{"mape":0.08,"coverage":0.91},"h12":{...}},"server":{...}}.
   */
  backtestMetrics: z.record(z.string(), z.unknown()),
  /**
   * SIL-1121 (Phase 3 tuning, parent SIL-1118): the fully resolved
   * tunable-parameter set this run fitted with (apps/forecast/src/forecast/
   * params.py). `{}` for every run written before db/schema/908 added the
   * column -- those rows predate params and must not be read as "ran with
   * today's defaults".
   */
  params: z.record(z.string(), z.unknown()),
  /**
   * SIL-1121: content hash of `params` (params.version()) -- the unit a
   * candidate-run tuning sweep ranks by. Null for every run written before
   * db/schema/908, and for any run whose params are otherwise unknown.
   */
  paramsVersion: z.string().nullable(),
});
export type FmvForecastRunDto = z.infer<typeof fmvForecastRunSchema>;

export const fmvForecastPointSchema = z.object({
  /** First day of the forecast month, 'YYYY-MM-DD'. */
  month: z.string(),
  medUsd: z.coerce.number(),
  loUsd: z.coerce.number(),
  hiUsd: z.coerce.number(),
  confidenceGrade: fmvForecastConfidenceGradeSchema,
  /**
   * true when the model judged this month below the confidence floor.
   * Suppressed points are still returned (never silently dropped) -- the
   * ops UI decides whether/how to render them.
   */
  suppressed: z.boolean(),
  /** Per-covariate contribution breakdown; shape defined by apps/forecast. */
  driverDecomposition: z.record(z.string(), z.unknown()),
});
export type FmvForecastPointDto = z.infer<typeof fmvForecastPointSchema>;

/**
 * SIL-1100: one realized-error row for the SKU page's "realized" table --
 * `v_fmv_forecast_error_row` (design doc §1.4/§2.2) narrowed to exactly the
 * columns the SKU page needs, across EVERY run (not just the latest live
 * run `points` above comes from) so an analyst can see how past runs
 * compared with comps that arrived later. `runKind` is included per row
 * (not filtered to `live`) so the table can label a backfill/candidate row
 * rather than silently mixing it in unlabeled.
 */
export const fmvForecastRealizedRowSchema = z.object({
  runId: z.string().uuid(),
  runKind: fmvForecastRunKindSchema,
  /** Calendar day (UTC) the run modeled, 'YYYY-MM-DD'. */
  asOf: z.string(),
  /** First day of the forecast month being compared, 'YYYY-MM-DD'. */
  targetMonth: z.string(),
  leadMonths: z.number(),
  predictedUsd: z.coerce.number(),
  loUsd: z.coerce.number(),
  hiUsd: z.coerce.number(),
  actualUsd: z.coerce.number(),
  priceKind: z.enum(['ask', 'executed', 'sold_ask']),
  pctErr: z.coerce.number(),
  inBand: z.boolean(),
  confidenceGrade: fmvForecastConfidenceGradeSchema,
  suppressed: z.boolean(),
  /**
   * SIL-1101: true when this row falls in the calibration-aware default
   * scope (`v_fmv_forecast_error_row.in_auto_scope`) -- surfaced (not
   * filtered) here so the SKU-page table can flag an out-of-scope row
   * rather than silently including or dropping it.
   */
  inAutoScope: z.boolean(),
  /**
   * SIL-1102: false for a row in the current (open, partial) calendar
   * month -- `v_fmv_forecast_error_row.month_closed`. Surfaced (not
   * filtered) here, same posture as `inAutoScope` -- the SKU-page table can
   * mark an open-month row "partial" rather than silently mixing it in
   * unlabeled or dropping it.
   */
  monthClosed: z.boolean(),
});
export type FmvForecastRealizedRowDto = z.infer<typeof fmvForecastRealizedRowSchema>;

export const skuFmvForecastResponseSchema = z.object({
  run: fmvForecastRunSchema,
  points: z.array(fmvForecastPointSchema),
  /**
   * SIL-1100: realized error rows for this SKU across all runs, newest
   * first, bounded to the latest 500. Empty (never omitted, never null)
   * when the SKU has no realized rows yet, or when the requesting role can
   * read the forecast but not `v_fmv_forecast_error_row` (defense in depth
   * -- see the route for the grant-boundary degrade).
   */
  realized: z.array(fmvForecastRealizedRowSchema),
});
export type SkuFmvForecastResponse = z.infer<typeof skuFmvForecastResponseSchema>;

export const fmvForecastRunsQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(20),
  /** Filter to the run(s) modeling this calendar day ('YYYY-MM-DD'); absent -> unfiltered. */
  asOf: z.string().optional(),
});
export type FmvForecastRunsQuery = z.infer<typeof fmvForecastRunsQuerySchema>;

export const fmvForecastRunListResponseSchema = z.object({
  runs: z.array(fmvForecastRunSchema),
});
export type FmvForecastRunListResponse = z.infer<typeof fmvForecastRunListResponseSchema>;

/**
 * Query params for GET /api/skus/:id/fmv-forecast. Absent `asOf` -> the
 * unconditional latest complete run (today's default behavior, unchanged);
 * present -> resolve the latest complete run tagged with that calendar day.
 */
export const skuFmvForecastQuerySchema = z.object({
  asOf: z.string().optional(),
});
export type SkuFmvForecastQuery = z.infer<typeof skuFmvForecastQuerySchema>;

/**
 * Client-safe forecast point (SIL-1070). Built as `fmvForecastPointSchema.
 * omit({ driverDecomposition: true })` so a future column leak on the ops
 * schema fails at this schema boundary too, not just at the
 * v_sku_fmv_forecast_client grant (db/schema/895_client_sku_fmv_forecast_
 * view.sql). `suppressed` is also omitted -- the client view already
 * excludes suppressed points at the database, so there is nothing for the
 * client DTO to carry a flag about.
 */
export const clientFmvForecastPointSchema = fmvForecastPointSchema.omit({
  driverDecomposition: true,
  suppressed: true,
});
export type ClientFmvForecastPointDto = z.infer<typeof clientFmvForecastPointSchema>;

/**
 * GET /api/client/skus/:skuId/fmv-forecast response. `run` is intentionally
 * a subset of `fmvForecastRunSchema` -- no `gitSha`, no `backtestMetrics`
 * (ops-internal reproducibility/accuracy detail) -- carrying only what the
 * "computed against the forecast run of `<asOf>`" stamping surface needs.
 */
export const clientFmvForecastRunSchema = z.object({
  id: z.string().uuid(),
  asOf: z.string(),
  modelVersion: z.string(),
});
export type ClientFmvForecastRunDto = z.infer<typeof clientFmvForecastRunSchema>;

export const clientSkuFmvForecastResponseSchema = z.object({
  run: clientFmvForecastRunSchema,
  points: z.array(clientFmvForecastPointSchema),
});
export type ClientSkuFmvForecastResponse = z.infer<typeof clientSkuFmvForecastResponseSchema>;

/**
 * SIL-1095 (Phase 1 tracer bullet): GET /api/fmv-forecast/accuracy.
 *
 * `groupBy` supports `'class_bucket'`, `'grade'` (SIL-1097), `'run'` /
 * `'model_version'` (SIL-1098), and `'sku'` (SIL-1099) -- the design doc
 * (docs/superpowers/specs/2026-09-16-fmv-forecast-accuracy-design.md §2.1)
 * lists all five. Extending this enum and adding a branch to the route's
 * group-key switch is meant to be the whole diff for a future groupBy --
 * this schema and the route are deliberately shaped so a new groupBy value
 * is an added case, not a rewrite.
 */
export const fmvForecastAccuracyGroupBySchema = z.enum([
  'class_bucket',
  'grade',
  'run',
  'model_version',
  'sku',
  'params_version',
]);
export type FmvForecastAccuracyGroupBy = z.infer<typeof fmvForecastAccuracyGroupBySchema>;

/**
 * SIL-1099: `groupBy=sku` sort key -- the "worst SKUs" table's own sort
 * control (design doc §2.1: "adds `sort` ... and `limit`"). `medianApe` and
 * `n` sort descending by value (biggest first); `bias` sorts descending by
 * `Math.abs(bias)` -- a large negative bias is just as much a "worst" miss
 * as a large positive one, and the table's purpose is finding the biggest
 * misses, not the most over- or under-priced SKUs specifically.
 */
export const fmvForecastAccuracySortSchema = z.enum(['medianApe', 'bias', 'n']);
export type FmvForecastAccuracySort = z.infer<typeof fmvForecastAccuracySortSchema>;

export const fmvForecastHorizonBucketSchema = z.enum(['h3', 'h6', 'h12', 'h24']);
export type FmvForecastHorizonBucket = z.infer<typeof fmvForecastHorizonBucketSchema>;

/**
 * SIL-1101: price-kind scope. `auto` (default) applies
 * `v_fmv_forecast_error_row.in_auto_scope` -- a point the engine rescaled
 * toward executed levels (a calibration multiplier not null and != 1) only
 * compares against `executed` comps, avoiding the false bias of comparing a
 * calibrated forecast against an `ask` comp. `all` disables the scope
 * entirely; `ask`/`executed` further restrict to one comp price kind
 * (composed with the `auto` scope, not instead of it -- see the route for
 * the exact filter chain).
 */
export const fmvForecastPriceKindScopeSchema = z.enum(['auto', 'all', 'ask', 'executed', 'sold_ask']);
export type FmvForecastPriceKindScope = z.infer<typeof fmvForecastPriceKindScopeSchema>;

/**
 * SIL-1102: whether the current (open, partial) calendar month is in scope.
 * `db/schema/902_fmv_forecast_open_month.sql` widened `v_fmv_forecast_error_
 * row`/`v_sku_month_actual` to include the open month (previously excluded
 * outright) -- every live run's `as_of` can fall in a month that is STILL
 * OPEN (the prod symptom this fixes: every filter returned zero rows while
 * the earliest forecast month, September 2026, was still open). `auto`
 * (default): apply every other filter first; if zero rows have
 * `monthClosed = true`, include the open-month rows so the page is not
 * empty; otherwise stay closed-only (a closed month is always preferred
 * when one exists -- an open month's numbers are still moving). `false`:
 * closed months only (the pre-902 behavior). `true`: no month filter at
 * all, regardless of whether closed rows exist.
 */
export const fmvForecastIncludeOpenMonthSchema = z.enum(['auto', 'true', 'false']);
export type FmvForecastIncludeOpenMonth = z.infer<typeof fmvForecastIncludeOpenMonthSchema>;

export const fmvForecastAccuracyQuerySchema = z.object({
  groupBy: fmvForecastAccuracyGroupBySchema.default('class_bucket'),
  /** asset_class_id, e.g. 'gpu' | 'server'. Absent -> every active class. */
  assetClass: z.string().optional(),
  /**
   * SIL-1097: narrow every groupBy slice (not just `groupBy=grade`) to one
   * confidence grade -- the ops "grade check" section's URL param, which
   * narrows the whole page (design doc's grade filter), not only its own
   * section.
   */
  grade: fmvForecastConfidenceGradeSchema.optional(),
  runKind: fmvForecastRunKindSchema.default('live'),
  /** Include suppressed forecast points. Default false -- matches the design doc's default scope. */
  includeSuppressed: z.coerce.boolean().default(false),
  /**
   * Count every complete run, not just the latest per (run_kind, as_of
   * calendar month). Default false -- v_fmv_forecast_error_row.
   * last_run_in_month is the filter this flips.
   */
  allRuns: z.coerce.boolean().default(false),
  /** SIL-1101: see fmvForecastPriceKindScopeSchema. Default 'auto'. */
  priceKind: fmvForecastPriceKindScopeSchema.default('auto'),
  /** SIL-1102: see fmvForecastIncludeOpenMonthSchema. Default 'auto'. */
  includeOpenMonth: fmvForecastIncludeOpenMonthSchema.default('auto'),
  /**
   * SIL-1099: narrows every groupBy slice (not just `groupBy=sku`) to the
   * variant SKUs of one parent (master) SKU -- the ops "worst SKUs" table's
   * parent-SKU filter is a whole-page URL param (design doc §3's "narrows
   * the table and the whole page to one family"), same pattern as `grade`
   * (SIL-1097) above. Applied directly against
   * `v_fmv_forecast_error_row.parent_sku_id`, which the view already
   * carries per row -- no join needed for this filter itself.
   */
  parentSkuId: z.string().uuid().optional(),
  /** SIL-1099: `groupBy=sku` sort key. Ignored by every other groupBy. */
  sort: fmvForecastAccuracySortSchema.default('medianApe'),
  /** SIL-1099: `groupBy=sku` row cap, applied after sorting. Ignored by every other groupBy. */
  limit: z.coerce.number().int().min(1).max(500).default(50),
});
export type FmvForecastAccuracyQuery = z.infer<typeof fmvForecastAccuracyQuerySchema>;

/**
 * One metric block -- per cell (e.g. one asset class x horizon bucket) or
 * the `total` block over every row in the filtered set. `medianApe` is the
 * primary metric (design doc §2.1); `mape` and `bias` (median signed
 * pct_err) are secondary. `coverage` is the share of rows with
 * `in_band = true`. All four are `null` when `n = 0` (no divide-by-zero --
 * an empty cell reports `n: 0`, not a fabricated zero rate).
 *
 * SIL-1096 baseline/skill-score fields:
 *   - `naiveMedianApe`: median of `naive_abs_pct_err` (v_fmv_forecast_error_
 *     row) -- the naive "last price carried forward" forecast's own error.
 *     Null when no row in the cell has a naive baseline (e.g. every SKU
 *     lacks 3+ comps in any prior closed month).
 *   - `skillScore`: `medianApe / naiveMedianApe`. Below 1 means the model
 *     beats the naive baseline. Null when `naiveMedianApe` is null OR 0
 *     (division would be undefined/infinite, not a fabricated score).
 *   - `monthlyMedianApe`: secondary metric -- median, across distinct
 *     (run, SKU, target month) triples in the cell, of
 *     `abs(predicted - monthly 'all' actual median) / monthly 'all' actual
 *     median` (v_sku_month_actual at the *target* month, not the naive
 *     baseline's prior month). Computed per run-SKU-month, not per comp,
 *     so a SKU-month with many comps does not out-weigh one with few.
 *
 * SIL-1101 `priceKind` interaction: `priceKind` (auto/all/ask/executed)
 * narrows only the per-comp rows this block aggregates over (`medianApe`,
 * `mape`, `bias`, `coverage`, `n`, `nSkus`) -- `naiveMedianApe` and
 * `monthlyMedianApe` deliberately keep comparing against
 * `v_sku_month_actual`'s `'all'` monthly median regardless of `priceKind`.
 * The naive baseline and the target-month actual are monthly aggregates
 * over every comp in that SKU-month, not one calibration-scoped subset --
 * splitting them by price kind would mean "naive" no longer means "last
 * month's actual price observed in the market", and a `priceKind=executed`
 * SKU-month with too few executed comps to reach `v_sku_month_actual`'s
 * `n_comps >= 3` floor would otherwise silently lose its baseline.
 */
export const fmvForecastAccuracyMetricsSchema = z.object({
  n: z.number().int(),
  nSkus: z.number().int(),
  medianApe: z.number().nullable(),
  mape: z.number().nullable(),
  bias: z.number().nullable(),
  coverage: z.number().nullable(),
  naiveMedianApe: z.number().nullable(),
  skillScore: z.number().nullable(),
  monthlyMedianApe: z.number().nullable(),
});
export type FmvForecastAccuracyMetrics = z.infer<typeof fmvForecastAccuracyMetricsSchema>;

/** One class x horizon-bucket cell -- the `groupBy=class_bucket` row shape. */
export const fmvForecastAccuracyClassBucketCellSchema = z.object({
  assetClassId: z.string(),
  horizonBucket: fmvForecastHorizonBucketSchema,
  metrics: fmvForecastAccuracyMetricsSchema,
});
export type FmvForecastAccuracyClassBucketCell = z.infer<typeof fmvForecastAccuracyClassBucketCellSchema>;

/**
 * SIL-1097: one confidence-grade cell -- the `groupBy=grade` row shape.
 * `threshold` is the engine's own MAPE threshold for this grade (`null` for
 * D, which has none -- see `GRADE_MAPE_THRESHOLDS` in
 * `fmv-forecast-grade-thresholds.ts`), and `exceedsThreshold` is `true` when
 * `metrics.medianApe` is realized to be worse than the threshold the engine
 * used to assign this grade -- computed server-side (not left to the UI) so
 * the flag can never drift from the metric it is judging. `null` medianApe
 * (n=0) never exceeds -- an empty cell is not a miss.
 */
export const fmvForecastAccuracyGradeCellSchema = z.object({
  grade: fmvForecastConfidenceGradeSchema,
  threshold: z.number().nullable(),
  exceedsThreshold: z.boolean(),
  metrics: fmvForecastAccuracyMetricsSchema,
});
export type FmvForecastAccuracyGradeCell = z.infer<typeof fmvForecastAccuracyGradeCellSchema>;

/**
 * SIL-1098: one run x horizon-bucket cell -- the `groupBy=run` row shape,
 * the "By run" trend view (design doc §3.4). Carries the run's own identity
 * columns (`asOf`, `modelVersion`, `runKind`) alongside `runId` so the ops
 * table doesn't need a second round-trip to `/fmv-forecast/runs` to label
 * each row -- one row per run as-of date, one column per horizon bucket.
 */
export const fmvForecastAccuracyRunCellSchema = z.object({
  runId: z.string().uuid(),
  asOf: z.string(),
  modelVersion: z.string(),
  runKind: fmvForecastRunKindSchema,
  horizonBucket: fmvForecastHorizonBucketSchema,
  /** SIL-1121: this run's resolved params and their content hash -- see fmvForecastRunSchema. */
  params: z.record(z.string(), z.unknown()),
  paramsVersion: z.string().nullable(),
  metrics: fmvForecastAccuracyMetricsSchema,
});
export type FmvForecastAccuracyRunCell = z.infer<typeof fmvForecastAccuracyRunCellSchema>;

/**
 * SIL-1121 (Phase 3 tuning, parent SIL-1118): one `params_version` cell --
 * the `groupBy=params_version` row shape, the tuning-sweep leaderboard
 * (design doc §3.1/§4 item 2). Pools every run sharing a `paramsVersion`
 * (typically a sweep of several `as_of` dates at the same candidate params)
 * into one row, since a single candidate run at one `as_of` is thin
 * evidence -- the same params swept over several dates is the comparison
 * worth making. `asOfMin`/`asOfMax` bound the dates actually swept;
 * `nRuns` is a distinct count of `run_id`, not row count (each run
 * contributes many error rows).
 */
export const fmvForecastAccuracyParamsVersionCellSchema = z.object({
  paramsVersion: z.string(),
  params: z.record(z.string(), z.unknown()),
  horizonBucket: fmvForecastHorizonBucketSchema,
  nRuns: z.number().int(),
  asOfMin: z.string(),
  asOfMax: z.string(),
  metrics: fmvForecastAccuracyMetricsSchema,
});
export type FmvForecastAccuracyParamsVersionCell = z.infer<
  typeof fmvForecastAccuracyParamsVersionCellSchema
>;

/**
 * SIL-1098: one model-version x horizon-bucket cell -- the
 * `groupBy=model_version` row shape, the alternate grouping for the "By
 * run" trend view's run/model-version selector (design doc §3.4) --
 * collapses every run sharing a `modelVersion` into one row, for comparing
 * model versions rather than individual run dates.
 */
export const fmvForecastAccuracyModelVersionCellSchema = z.object({
  modelVersion: z.string(),
  horizonBucket: fmvForecastHorizonBucketSchema,
  metrics: fmvForecastAccuracyMetricsSchema,
});
export type FmvForecastAccuracyModelVersionCell = z.infer<
  typeof fmvForecastAccuracyModelVersionCellSchema
>;

/**
 * SIL-1099: one variant-SKU cell -- the `groupBy=sku` row shape, the "worst
 * SKUs" table (design doc §3.5). `skuName` uses the ops SKU display-name
 * convention (`sku.display_name`, same column every other ops table reads).
 * `parentSkuId`/`parentSkuName` are both nullable -- a standalone variant
 * (grain='variant' with no family, per `sku.grain`'s own doc comment) has no
 * parent, not an error. Master SKUs (`grain='master'`) never produce a cell
 * here -- the route joins `sku` and excludes them outright, so "no master
 * SKU appears as a row" is enforced server-side, not left to the UI to
 * filter.
 */
export const fmvForecastAccuracySkuCellSchema = z.object({
  skuId: z.string().uuid(),
  skuName: z.string(),
  assetClassId: z.string(),
  parentSkuId: z.string().uuid().nullable(),
  parentSkuName: z.string().nullable(),
  metrics: fmvForecastAccuracyMetricsSchema,
});
export type FmvForecastAccuracySkuCell = z.infer<typeof fmvForecastAccuracySkuCellSchema>;

/**
 * The full set of `groupBy`-dependent row shapes `groups` can return. A
 * discriminated union rather than one flattened shape: each groupBy carries
 * different identity columns.
 */
export const fmvForecastAccuracyGroupCellSchema = z.union([
  fmvForecastAccuracyClassBucketCellSchema,
  fmvForecastAccuracyGradeCellSchema,
  fmvForecastAccuracyRunCellSchema,
  fmvForecastAccuracyModelVersionCellSchema,
  fmvForecastAccuracySkuCellSchema,
  fmvForecastAccuracyParamsVersionCellSchema,
]);
export type FmvForecastAccuracyGroupCell = z.infer<typeof fmvForecastAccuracyGroupCellSchema>;

export const fmvForecastAccuracyResponseSchema = z.object({
  groupBy: fmvForecastAccuracyGroupBySchema,
  groups: z.array(fmvForecastAccuracyGroupCellSchema),
  total: fmvForecastAccuracyMetricsSchema,
  /**
   * SIL-1102: true when open-(current-)month rows were actually included
   * in this response's scope -- not merely whether `includeOpenMonth`
   * requested them. `includeOpenMonth=false` -> always false.
   * `includeOpenMonth=true` -> true only if at least one in-scope row has
   * `monthClosed=false`. `includeOpenMonth=auto` (default) -> true only
   * when the auto policy actually fell back to the open month (zero closed
   * rows existed in scope). The ops UI shows its "partial month" notice
   * exactly when this is true.
   */
  includesOpenMonth: z.boolean(),
  /**
   * SIL-1099: only populated when `groupBy=sku` -- the master SKUs that have
   * at least one variant with an error row in the current filter scope
   * (excluding `parentSkuId` itself, so the dropdown keeps listing every
   * family even while one is selected). "a small endpoint or an option list
   * returned alongside groupBy=sku" (design doc §3.5) -- this is the latter.
   */
  parentSkuOptions: z
    .array(z.object({ id: z.string().uuid(), displayName: z.string() }))
    .optional(),
  /**
   * SIL-1103: when the underlying data was last refreshed -- the accuracy
   * endpoint now reads a materialized snapshot (mv_fmv_forecast_error_row,
   * db/schema/905), not a live view, so "now" and "as of" have diverged.
   * ISO 8601 timestamp, or null when the snapshot has never been refreshed
   * (an empty MV -- v_fmv_forecast_error_row_refresh.refreshed_at is a bare
   * `max()`, which is null over zero rows). The ops page shows "Data as of
   * <refreshedAt>" and warns when it is more than 48h old.
   */
  refreshedAt: z.string().nullable(),
});
export type FmvForecastAccuracyResponse = z.infer<typeof fmvForecastAccuracyResponseSchema>;
